// src/utils/PerformanceCounter.ts
type Metrics = {
  count: number;
  total: number;
  min: number;
  max: number;
};

export class PerformanceCounter {
  private static enabled = false;
  private static metrics: Record<string, Metrics> = {};

  /** Turn performance counting on */
  static enable() {
    PerformanceCounter.enabled = true;
    console.info('[PerformanceCounter] enabled');
  }

  /** Turn performance counting off */
  static disable() {
    PerformanceCounter.enabled = false;
    console.info('[PerformanceCounter] disabled');
  }

  /** Internal: record a timing under the composite key "METHOD endpoint" */
  private static record(method: string, endpoint: string, duration: number) {
    if (!PerformanceCounter.enabled) return;
    const key = `${method} ${endpoint}`;
    let m = PerformanceCounter.metrics[key];
    if (!m) {
      m = { count: 0, total: 0, min: duration, max: duration };
      PerformanceCounter.metrics[key] = m;
    }
    m.count++;
    m.total += duration;
    m.min = Math.min(m.min, duration);
    m.max = Math.max(m.max, duration);
  }

  /**
   * Wrap an async call, tagging it with both method and endpoint.
   * @param method  A simple label like "GET" or "POST"
   * @param endpoint  The URL being called
   * @param fn  The actual async work (e.g. axios request)
   */
  static async wrap<T>(method: string, endpoint: string, fn: () => Promise<T>): Promise<T> {
    if (!PerformanceCounter.enabled) {
      return fn();
    }
    const start = performance.now();
    try {
      return await fn();
    } finally {
      const end = performance.now();
      PerformanceCounter.record(method, endpoint, end - start);
    }
  }

  /** Print a report of all collected metrics to the console */
  static report() {
    if (!PerformanceCounter.enabled) {
      console.warn('[PerformanceCounter] is disabled');
      return;
    }
    console.groupCollapsed('[PerformanceCounter] Report');
    for (const [key, m] of Object.entries(PerformanceCounter.metrics)) {
      console.log(
        `${key}: count=${m.count}, avg=${(m.total / m.count).toFixed(2)}ms, ` +
          `min=${m.min.toFixed(2)}ms, max=${m.max.toFixed(2)}ms`
      );
    }
    console.groupEnd();
  }
}

// Expose to window so you can toggle and report in the console:
typeof window != 'undefined' && ((window as any).PerfCounter = PerformanceCounter);
